import * as yup from 'yup'
import { CenteredFormCard, Checkbox, Column, Grid, Link, Row, TextField } from '../../base'
import { FieldPath } from 'react-hook-form/dist/types/path'
import { RegisterRequestData, useSignUp } from '../../../auth/auth'
import { Trans, useTranslation } from 'next-i18next'
import { handleFormErrorsFromAxios } from '../../../utils/Helper'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import LoadingSpinIcon from '../../../assets/img/loading-spin-icon.svg'
import NextLink from 'next/link'
import QuestionCircleIcon from '../../../assets/img/question-circle-icon.svg'
import React, { ChangeEvent } from 'react'
import Tooltip from '../../base/tooltip/Tooltip'
import useAresDetails from '../../../hooks/api/ares/useAresDetails'
import validator from 'validator'

const RegistrationCard = () => {
    const { t } = useTranslation('registration')
    const { signUp, isLoading } = useSignUp<RegisterRequestData>()

    const {
        handleSubmit,
        register,
        formState: { errors },
        setError,
        setValue,
        clearErrors
    } = useForm<RegisterRequestData>({
        resolver: yupResolver(
            yup.object({
                name: yup.string().required(t('registrationPage.form.name.validation.required')),
                email: yup.string()
                    .test('is-email',
                        t('registrationPage.form.email.validation.notValid'),
                        value => {
                            return validator.isEmail(value || '')
                        }
                    )
                    .required(t('registrationPage.form.email.validation.required')),
                companyName: yup.string().required(t('registrationPage.form.companyName.validation.required')),
                street: yup.string().required(t('registrationPage.form.street.validation.required')),
                buildingNumber: yup.string().required(t('registrationPage.form.buildingNumber.validation.required')),
                city: yup.string().required(t('registrationPage.form.city.validation.required')),
                postalCode: yup.string().required(t('registrationPage.form.postalCode.validation.required')),
                companyId: yup.string().required(t('registrationPage.form.cin.validation.required')),
                agreement: yup.boolean().isTrue(t('registrationPage.form.acceptTermsAndConditions.validation.required')),
                password: yup.string().required(t('registrationPage.form.password.validation.required'))
            })
        ),
        reValidateMode: 'onChange'
    })

    const { fetchAresDetails, isLoading: isAresLoading, notFound: aresDetailsNotFound } = useAresDetails(({ companyName, street, buildingNumber, city, postalCode }) => {
        clearErrors(['companyName', 'street', 'city', 'postalCode'])
        setValue('companyName', companyName)
        setValue('street', street)
        setValue('buildingNumber', buildingNumber)
        setValue('city', city)
        setValue('postalCode', postalCode)
    })

    const debouncedFetchAresDetails = async ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        if (value.length >= 8) {
            return fetchAresDetails(value, false)
        }
    }

    const getFieldError = (path: FieldPath<RegisterRequestData>) => {
        return errors[path]?.message
    }

    const submit = (data: RegisterRequestData) => {
        return !aresDetailsNotFound && signUp(data)
            .catch((error) => {
                return handleFormErrorsFromAxios(error, setError)
            })
    }

    const renderTitle = () => {
        return (
            <h1 tw='text-h1 text-primary-400'>{t('registrationPage.title')}</h1>
        )
    }

    const renderHeader = () => {
        return (
            <img src='/logo.svg' alt='Logo' />
        )
    }

    const renderDescription = () => {
        return (
            <p tw='text-normal font-normal text-primary-400'>
                {t('registrationPage.login.description')}&nbsp;
                <NextLink href='/' passHref>
                    <Link tw='text-normal'>{t('registrationPage.login.link')}</Link>
                </NextLink>
            </p>
        )
    }

    const renderAcceptTermsAndConditionsLabel = () => {
        const link = (url: string) => {
            return (
                <Link tw='text-small font-medium' underline={false} bold={false} href={url} />
            )
        }

        const components = {
            termsLink: link('/terms-and-condition'),
            gdprLink: link('/gdpr')
        }

        return (
            <span tw='text-small font-medium'>
                <Trans
                    t={t}
                    tw='text-small font-medium'
                    i18nKey='registrationPage.form.acceptTermsAndConditions.label'
                    components={components}
                />
            </span>
        )
    }

    const renderCompanyIdTooltip = () => {
        return (
            <Tooltip
                zIndex={999}
                placement='right'
                contentWidth='19em'
                contentPadding='0.5rem'
                content={
                    <span>
                        {t('registrationPage.form.cin.tooltip')}
                    </span>
                }
            >
                <QuestionCircleIcon />
            </Tooltip>
        )
    }

    return (
        <CenteredFormCard
            onSubmit={handleSubmit(submit)}
            submitLabel={t('registrationPage.form.submit')}
            loading={isLoading}
            title={renderTitle()}
            header={renderHeader()}
            description={renderDescription()}
        >
            <Grid>
                <Row>
                    <Column cols={12}>
                        <TextField
                            label={t('registrationPage.form.name.label')}
                            invalidMessage={getFieldError('name')}
                            {...register('name')}
                        />
                    </Column>
                    <Column cols={12}>
                        <TextField
                            label={t('registrationPage.form.cin.label')}
                            invalidMessage={
                                aresDetailsNotFound
                                    ? t('registrationPage.form.cin.validation.notFound')
                                    : getFieldError('companyId')
                            }
                            leftIcon={isAresLoading && <LoadingSpinIcon className='animate-spin fill-primary-300' />}
                            rightIcon={renderCompanyIdTooltip()}
                            {...register('companyId', { onChange: debouncedFetchAresDetails })}
                        />
                    </Column>
                    <Column cols={12}>
                        <TextField
                            label={t('registrationPage.form.companyName.label')}
                            disabled
                            invalidMessage={getFieldError('companyName')}
                            {...register('companyName')}
                        />
                    </Column>
                    <Column cols={8}>
                        <TextField
                            label={t('registrationPage.form.street.label')}
                            disabled
                            invalidMessage={getFieldError('street')}
                            {...register('street')}
                        />
                    </Column>
                    <Column cols={4}>
                        <TextField
                            label={t('registrationPage.form.buildingNumber.label')}
                            disabled
                            invalidMessage={getFieldError('buildingNumber')}
                            {...register('buildingNumber')}
                        />
                    </Column>
                    <Column cols={6}>
                        <TextField
                            label={t('registrationPage.form.city.label')}
                            disabled
                            invalidMessage={getFieldError('city')}
                            {...register('city')}
                        />
                    </Column>
                    <Column cols={6}>
                        <TextField
                            label={t('registrationPage.form.postalCode.label')}
                            disabled
                            invalidMessage={getFieldError('postalCode')}
                            {...register('postalCode')}
                        />
                    </Column>
                    <Column cols={12}>
                        <TextField
                            label={t('registrationPage.form.email.label')}
                            invalidMessage={getFieldError('email')}
                            {...register('email')}
                        />
                    </Column>
                    <Column cols={12}>
                        <TextField
                            label={t('registrationPage.form.password.label')}
                            type='password'
                            invalidMessage={getFieldError('password')}
                            {...register('password')}
                        />
                    </Column>
                    <Column cols={12}>
                        <Checkbox
                            label={renderAcceptTermsAndConditionsLabel()}
                            invalidMessage={getFieldError('agreement')}
                            {...register('agreement')}
                        />
                    </Column>
                </Row>
            </Grid>
        </CenteredFormCard>
    )
}

export default RegistrationCard
